import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { adminAuthHeaders } from "@/components/AdminGate";
import useNewStratagemForm from "./useNewStratagemForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getNextHour = () => {
  const now = new Date();
  now.setHours(now.getHours() + 1);
  now.setMinutes(0);
  now.setSeconds(0);
  now.setMilliseconds(0);
  return now.toISOString().slice(0, 16);
};

export const formatDate = (isoString) => {
  if (!isoString) return "--";
  try {
    return new Date(isoString).toLocaleString();
  } catch {
    return isoString;
  }
};

const EMPTY_EDIT = { name: "", category: "", code: [], categories: [] };

export default function useReviewData() {
  const [reviewItems, setReviewItems] = useState([]);
  const [pendingItems, setPendingItems] = useState([]);
  const [actionHistory, setActionHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [editingId, setEditingId] = useState(null);
  const [editingValues, setEditingValues] = useState(EMPTY_EDIT);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [categoryInput, setCategoryInput] = useState("");
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [approveSettings, setApproveSettings] = useState({});

  const activeItems = useMemo(
    () =>
      reviewItems
        .filter((i) => !i.is_deleted && i.is_active !== false && !i.pending_review)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [reviewItems],
  );

  const deletedItems = useMemo(
    () => reviewItems.filter((i) => i.is_deleted).sort((a, b) => a.name.localeCompare(b.name)),
    [reviewItems],
  );

  const allCategories = useMemo(() => {
    const cats = new Set();
    reviewItems.forEach((i) => {
      if (i.category) cats.add(i.category);
      if (i.categories) i.categories.forEach((c) => cats.add(c));
    });
    return Array.from(cats).sort();
  }, [reviewItems]);

  const filtered = useMemo(() => {
    let list = activeItems.filter((i) => i.name.toLowerCase().includes(search.toLowerCase()));
    if (filter === "new") list = list.filter((i) => i.is_new);
    else if (filter !== "all")
      list = list.filter((i) => i.category === filter || (i.categories && i.categories.includes(filter)));
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [activeItems, search, filter]);

  const newCount = useMemo(() => activeItems.filter((i) => i.is_new).length, [activeItems]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [reviewRes, pendingRes, historyRes, statusRes] = await Promise.all([
        axios.get(`${API}/stratagems/review`),
        axios.get(`${API}/stratagems/pending`),
        axios.get(`${API}/stratagems/history`),
        axios.get(`${API}/stratagems/sync-status`),
      ]);
      setReviewItems(reviewRes.data || []);
      setPendingItems(pendingRes.data || []);
      setActionHistory(historyRes.data || []);
      setSyncStatus(statusRes.data);
    } catch {
      setError("Unable to load review data.");
    } finally {
      setLoading(false);
    }
  }, []);

  const beginEdit = useCallback((item) => {
    setEditingId(item.id);
    setEditingValues({
      name: item.name || "",
      category: item.category || "",
      code: item.code ? [...item.code] : [],
      categories: item.categories ? [...item.categories] : [],
    });
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditingValues(EMPTY_EDIT);
    setCategoryInput("");
  }, []);

  const addDirection = useCallback(
    (d) => setEditingValues((p) => ({ ...p, code: [...p.code, d] })),
    [],
  );
  const removeLastDirection = useCallback(
    () => setEditingValues((p) => ({ ...p, code: p.code.slice(0, -1) })),
    [],
  );
  const clearDirections = useCallback(
    () => setEditingValues((p) => ({ ...p, code: [] })),
    [],
  );

  const addCategory = useCallback(() => {
    const cat = categoryInput.trim();
    if (!cat) return;
    setEditingValues((p) => {
      if (p.categories.includes(cat)) return p;
      return { ...p, categories: [...p.categories, cat] };
    });
    setCategoryInput("");
  }, [categoryInput]);

  const removeCategory = useCallback(
    (cat) => setEditingValues((p) => ({ ...p, categories: p.categories.filter((c) => c !== cat) })),
    [],
  );

  const saveOverride = useCallback(async (item) => {
    const payload = {};
    const trimmedName = editingValues.name.trim();
    const trimmedCategory = editingValues.category.trim();
    if (trimmedName && trimmedName !== item.name) payload.name = trimmedName;
    if (trimmedCategory && trimmedCategory !== item.category) payload.category = trimmedCategory;
    if (editingValues.categories.length > 0) payload.categories = editingValues.categories;
    const hasCodeChange =
      editingValues.code.length > 0 && JSON.stringify(editingValues.code) !== JSON.stringify(item.code);
    if (hasCodeChange) payload.code = editingValues.code;
    if (Object.keys(payload).length === 0) {
      toast.error("No changes to save.");
      return;
    }
    try {
      await axios.patch(`${API}/stratagems/${item.id}`, payload, { headers: adminAuthHeaders() });
      toast.success("Changes saved.");
      cancelEdit();
      fetchAllData();
    } catch {
      toast.error("Unable to save changes.");
    }
  }, [editingValues, cancelEdit, fetchAllData]);

  const deleteStratagem = useCallback(async (item) => {
    if (!window.confirm(`Delete ${item.name}?`)) return;
    try {
      await axios.delete(`${API}/stratagems/${item.id}`, { headers: adminAuthHeaders() });
      toast.success("Stratagem deleted.");
      fetchAllData();
    } catch {
      toast.error("Unable to delete stratagem.");
    }
  }, [fetchAllData]);

  const undoStratagem = useCallback(async (stratagemId, name) => {
    const label = name || stratagemId;
    if (!window.confirm(`Undo last change for ${label}?`)) return;
    try {
      await axios.post(`${API}/stratagems/${stratagemId}/undo`);
      toast.success("Undo completed.");
      fetchAllData();
    } catch {
      toast.error("Unable to undo change.");
    }
  }, [fetchAllData]);

  const initApproveSettings = useCallback((item) => {
    setApproveSettings((prev) => {
      if (prev[item.id]) return prev;
      return { ...prev, [item.id]: { releaseDate: getNextHour(), newDays: 15 } };
    });
  }, []);

  const updateApproveSettings = useCallback(
    (id, field, value) => setApproveSettings((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } })),
    [],
  );

  const approveStratagem = useCallback(async (item) => {
    const settings = approveSettings[item.id] || { releaseDate: getNextHour(), newDays: 15 };
    try {
      await axios.post(
        `${API}/stratagems/${item.id}/approve`,
        { release_date: new Date(settings.releaseDate).toISOString(), new_days: parseInt(settings.newDays, 10) },
        { headers: adminAuthHeaders() },
      );
      toast.success(`${item.name} approved for release.`);
      fetchAllData();
    } catch {
      toast.error("Unable to approve stratagem.");
    }
  }, [approveSettings, fetchAllData]);

  const handleRefresh = useCallback(async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/stratagems/refresh`, {}, { headers: adminAuthHeaders() });
      toast.success("Sync completed from GitHub.");
      fetchAllData();
    } catch {
      toast.error("Sync failed.");
    } finally {
      setSyncing(false);
    }
  }, [fetchAllData]);

  const handleAutoAssignCategories = useCallback(async () => {
    try {
      const res = await axios.post(`${API}/categories/auto-assign`, {}, { headers: adminAuthHeaders() });
      toast.success(`Auto-assigned categories to ${res.data.updated} stratagems.`);
      fetchAllData();
    } catch {
      toast.error("Auto-assign failed.");
    }
  }, [fetchAllData]);

  const newForm = useNewStratagemForm(fetchAllData);

  useEffect(() => { fetchAllData(); }, [fetchAllData]);
  useEffect(() => { pendingItems.forEach((i) => initApproveSettings(i)); }, [pendingItems, initApproveSettings]);

  return {
    filtered, pendingItems, actionHistory, activeItems, deletedItems, allCategories, newCount,
    loading, error, syncStatus, syncing,
    search, setSearch, filter, setFilter,
    showCategoryManager, setShowCategoryManager, showHistory, setShowHistory,
    editingId, editingValues, setEditingValues, categoryInput, setCategoryInput,
    beginEdit, cancelEdit, addDirection, removeLastDirection, clearDirections,
    addCategory, removeCategory, saveOverride, deleteStratagem, undoStratagem,
    approveSettings, updateApproveSettings, approveStratagem,
    handleRefresh, handleAutoAssignCategories,
    showAddModal: newForm.showModal,
    setShowAddModal: newForm.setShowModal,
    newStratagem: newForm.form,
    setNewStratagem: newForm.setForm,
    newCategoryInput: newForm.categoryInput,
    setNewCategoryInput: newForm.setCategoryInput,
    resetNewStratagem: newForm.reset,
    addNewDirection: newForm.addDirection,
    removeNewLastDirection: newForm.removeLastDirection,
    clearNewDirections: newForm.clearDirections,
    addNewCategory: newForm.addCategory,
    removeNewCategory: newForm.removeCategory,
    createStratagem: newForm.create,
    fetchAllData,
  };
}
